import * as React from "react";

import { useTranslation } from "react-i18next";

import { TriangleAlert, XIcon } from "lucide-react";

import { asIcon, Icon, IconData } from "simplydo/react/ui/icon";
import { Input, type InputProps } from "simplydo/react/ui/input";
import { Popup } from "simplydo/react/ui/popup";

import { Button } from "../../button";
import type { DateFieldProps } from "../inputs/BaseInput";
import type { ValidationResult } from "../inputs/validate";
import checkIE from "../lib/checkIE";
import checkMobile from "../lib/checkMobile";
import { invoke } from "../lib/utils";

const DEFAULT_VALIDATION_DEBOUNCE_MS = 300;
const ERROR_ICON_COLOR = "#9f3a38";

type InputWithRefProps = InputProps & {
  clearable?: boolean;
  onClear?: (e: React.SyntheticEvent<HTMLElement>) => void;
  innerRef?: (node: HTMLInputElement | null) => void;
  errorMessage?: string | null;
};

const ClearButton = ({
  onClear,
}: {
  onClear?: (e: React.SyntheticEvent<HTMLElement>) => void;
}) => {
  const { t } = useTranslation();
  return (
    <Button
      size="small"
      variant="ghost"
      aria-label={t("date.clear")}
      onClick={onClear}
    >
      <Icon icon={XIcon} />
    </Button>
  );
};

class InputWithRef extends React.Component<InputWithRefProps> {
  public render() {
    const {
      value,
      clearable,
      icon,
      onClear,
      innerRef,
      onFocus,
      onBlur,
      onClick,
      onMouseEnter,
      label: _label,
      errorMessage,
      ...rest
    } = this.props;

    const errorIcon = errorMessage ? (
      <span
        onClick={(e) => e.stopPropagation()}
        style={{ color: ERROR_ICON_COLOR, display: "inline-flex" }}
      >
        <Popup
          trigger={<Icon icon={TriangleAlert} />}
          on="hover"
          position="top center"
        >
          {errorMessage}
        </Popup>
      </span>
    ) : null;

    const resolvedIcon = errorIcon ? (
      errorIcon
    ) : value && clearable ? (
      <ClearButton onClear={onClear} />
    ) : icon && icon !== true ? (
      asIcon(icon)
    ) : undefined;

    return (
      <Input
        {...rest}
        onFocus={onFocus}
        onBlur={onBlur}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        icon={resolvedIcon}
        value={value}
        input={{ ref: innerRef }}
      />
    );
  }
}

export type InputViewProps = DateFieldProps & {
  /** Used for passing input dom node (input field or inline calendar) to parent component. */
  onMount: (e: HTMLElement) => void;
  /** Called after a validated (or empty) input value is committed. */
  onChange: (
    e: React.SyntheticEvent<HTMLElement>,
    data: { value: string },
  ) => void;
  /**
   * Called when the input loses focus.
   * Pending drafts are validated on blur (committed if valid).
   */
  onBlur?: () => void;
  closePopup: () => void;
  openPopup: () => void;
  /** Called when the input gains focus. */
  onFocus?: () => void;
  /** Called when the popup closes. */
  onClose?: () => void;
  /** Function for rendering picker. */
  renderPicker: () => React.ReactNode;
  /** Called after clear icon has clicked. */
  onClear?: (
    e: React.SyntheticEvent<HTMLElement>,
    data: { value: string },
  ) => void;
  /** Whether to close a popup when cursor leaves it. */
  closeOnMouseLeave?: boolean;
  /** A field can have its label next to instead of above it. */
  inlineLabel?: boolean;
  /** Using the clearable setting will let users remove their selection from a calendar. */
  clearable?: boolean;
  /** Optional Icon to display inside the Input. */
  icon?: IconData | boolean | false;
  /** Icon position. Default: 'right'. */
  iconPosition?: "left" | "right";
  /** Whether popup is closed. */
  popupIsClosed?: boolean;
  /** Whether to display inline picker or picker inside a popup. */
  inline?: boolean;
  /** Duration of the CSS transition animation in milliseconds. */
  duration?: number;
  /** Where to display popup. */
  popupPosition?:
    | "top left"
    | "top right"
    | "bottom right"
    | "bottom left"
    | "right center"
    | "left center"
    | "top center"
    | "bottom center";
  /** Currently selected value (always a display string). */
  value?: string;
  /** Picker width (any value that `style.width` can take). */
  pickerWidth?: string;
  /** Style object for picker. */
  pickerStyle?: object;
  /** Do not display popup if true. */
  readOnly?: boolean;
  /** Try to prevent mobile keyboard appearing. */
  hideMobileKeyboard?: boolean;
  /** Validate typed text; onChange only fires when this returns valid. */
  validate?: (raw: string) => ValidationResult;
  /** Debounce before showing a validation error while typing. Default: 300ms. */
  validationDebounceMs?: number;
  children?: React.ReactNode;
};

type InputViewState = {
  draftValue: string;
  errorMessage: string | null;
  isEditing: boolean;
};

class InputView extends React.Component<InputViewProps, InputViewState> {
  public static defaultProps = {
    inline: false,
    closeOnMouseLeave: true,
    tabIndex: 0,
    clearable: false,
    duration: 200,
    iconPosition: "right" as const,
    validationDebounceMs: DEFAULT_VALIDATION_DEBOUNCE_MS,
  };

  private inputNode: HTMLElement | null | undefined;
  private popupNode: HTMLElement | null | undefined;
  private mouseLeaveTimeout: number | null = null;
  private validationTimeout: number | null = null;

  constructor(props: InputViewProps) {
    super(props);
    this.state = {
      draftValue: props.value ?? "",
      errorMessage: null,
      isEditing: false,
    };
  }

  public componentDidUpdate(prevProps: InputViewProps) {
    // Sync draft when the committed value changes (picker select, parent update).
    if (prevProps.value !== this.props.value) {
      this.clearValidationTimeout();
      this.setState({
        draftValue: this.props.value ?? "",
        errorMessage: null,
        isEditing: false,
      });
    }
  }

  public componentWillUnmount() {
    this.clearValidationTimeout();
    if (this.mouseLeaveTimeout) {
      clearTimeout(this.mouseLeaveTimeout);
    }
  }

  private clearValidationTimeout = () => {
    if (this.validationTimeout != null) {
      clearTimeout(this.validationTimeout);
      this.validationTimeout = null;
    }
  };

  private isInsidePicker = (target: EventTarget | null): boolean => {
    if (!(target instanceof Node)) {
      return false;
    }
    return Boolean(
      this.popupNode?.contains(target) || this.inputNode?.contains(target),
    );
  };

  public onBlur = (e: React.FocusEvent<HTMLElement>) => {
    const { closePopup } = this.props;

    if (this.isInsidePicker(e.relatedTarget) || checkIE()) {
      return;
    }

    // Flush pending validation so a blur→submit sequence commits a valid
    // draft (or surfaces an error) before the parent reads state.
    if (this.state.isEditing || this.validationTimeout != null) {
      const raw = this.state.draftValue;
      this.clearValidationTimeout();
      this.runValidation(e, raw);
    }

    this.setState({ isEditing: false });
    invoke(this.props, "onBlur");
    closePopup();
  };

  public onMouseLeave = (
    e: React.MouseEvent<HTMLElement>,
    ...args: unknown[]
  ) => {
    const { closeOnMouseLeave, closePopup } = this.props;

    if (!this.isInsidePicker(e.relatedTarget) && closeOnMouseLeave) {
      invoke(this.props, "onMouseLeave", e, ...args);
      this.mouseLeaveTimeout = window.setTimeout(() => {
        if (this.mouseLeaveTimeout) {
          closePopup();
        }
      }, 500);
    }
  };

  public onMouseEnter = (
    e: React.MouseEvent<HTMLElement>,
    ...args: unknown[]
  ) => {
    const { closeOnMouseLeave } = this.props;

    invoke(this.props, "onMouseEnter", e, ...args);
    if (
      e.currentTarget === this.popupNode ||
      e.currentTarget === this.inputNode
    ) {
      if (closeOnMouseLeave) {
        clearTimeout(this.mouseLeaveTimeout!);
        this.mouseLeaveTimeout = null;
      }
    }
  };

  private commitValue = (
    e: React.SyntheticEvent<HTMLElement>,
    value: string,
  ) => {
    this.clearValidationTimeout();
    this.setState({ draftValue: value, errorMessage: null, isEditing: false });
    this.props.onChange(e, { value });
  };

  private runValidation = (
    e: React.SyntheticEvent<HTMLElement>,
    raw: string,
  ) => {
    const { validate, onChange } = this.props;
    if (!validate) {
      this.setState({ errorMessage: null, isEditing: false });
      onChange(e, { value: raw });
      return;
    }

    const result = validate(raw);
    if (result.valid) {
      this.commitValue(e, result.value);
      return;
    }

    this.setState({ errorMessage: result.message, isEditing: true });
  };

  private onTypedChange = (
    e: React.SyntheticEvent<HTMLElement>,
    { value }: { value: string },
  ) => {
    const {
      validate,
      validationDebounceMs,
      onChange,
      closePopup,
      popupIsClosed,
    } = this.props;

    if (!popupIsClosed) {
      closePopup();
    }

    if (!validate) {
      this.setState({ draftValue: value, errorMessage: null });
      onChange(e, { value });
      return;
    }

    this.clearValidationTimeout();

    // Commit immediately when valid so parent state stays in sync (no
    // debounce→submit race). Debounce only the error message for incomplete
    // or invalid drafts to avoid flicker while typing.
    const result = validate(value);
    if (result.valid) {
      this.commitValue(e, result.value);
      return;
    }

    this.setState({ draftValue: value, isEditing: true });
    this.validationTimeout = window.setTimeout(() => {
      this.validationTimeout = null;
      this.setState({ errorMessage: result.message, isEditing: true });
    }, validationDebounceMs ?? DEFAULT_VALIDATION_DEBOUNCE_MS);
  };

  private onClear = (e: React.SyntheticEvent<HTMLElement>) => {
    const { onClear, onChange } = this.props;
    this.clearValidationTimeout();
    this.setState({ draftValue: "", errorMessage: null, isEditing: false });
    (onClear || onChange)(e, { value: "" });
  };

  public render() {
    const {
      renderPicker,
      popupPosition,
      inline,
      value: _value,
      closeOnMouseLeave,
      onChange: _onChange,
      onClear: _onClear,
      children: _children,
      inlineLabel: _inlineLabel,
      popupIsClosed,
      tabIndex,
      onMount,
      closePopup: _closePopup,
      openPopup,
      duration: _duration,
      pickerWidth: _pickerWidth,
      pickerStyle: _pickerStyle,
      onClose: _onClose,
      iconPosition,
      icon,
      readOnly,
      hideMobileKeyboard,
      validate: _validate,
      validationDebounceMs: _validationDebounceMs,
      error: errorProp,
      ...fieldRest
    } = this.props;

    const { label: _label, ...inputRest } = fieldRest;
    const { draftValue, errorMessage } = this.state;
    const showError = Boolean(errorMessage) || Boolean(errorProp);

    const inputElement = (
      <InputWithRef
        {...inputRest}
        // trying to use this hack https://stackoverflow.com/a/7610923 for hiding mobile keyboard
        readOnly={(checkMobile() && hideMobileKeyboard) || readOnly}
        icon={icon === false ? undefined : icon}
        iconPosition={
          icon && iconPosition !== "right" ? iconPosition : undefined
        }
        innerRef={(e) => {
          this.inputNode = e;
          if (e) {
            onMount(e);
          }
        }}
        value={draftValue}
        error={showError}
        errorMessage={errorMessage}
        tabIndex={tabIndex}
        clearable={this.props.clearable}
        onClear={this.onClear}
        onFocus={() => {
          invoke(this.props, "onFocus");
          openPopup();
        }}
        onClick={() => {
          invoke(this.props, "onFocus");
          openPopup();
        }}
        onBlur={this.onBlur}
        onMouseEnter={this.onMouseEnter}
        onChange={this.onTypedChange}
      />
    );

    if (inline) {
      return renderPicker();
    }

    return (
      <>
        {inputElement}
        {!readOnly && (
          <Popup
            open={!popupIsClosed}
            trigger={null}
            position={popupPosition}
            hoverable={closeOnMouseLeave}
            fluid
            transparent
            context={this.inputNode}
            on="hover"
          >
            <div
              onBlur={this.onBlur}
              onMouseLeave={this.onMouseLeave}
              onMouseEnter={this.onMouseEnter}
              style={{ outline: "none" }}
              tabIndex={0}
              ref={(ref) => {
                this.popupNode = ref;
              }}
            >
              {renderPicker()}
            </div>
          </Popup>
        )}
      </>
    );
  }

  public scrollListener = () => {
    const { closePopup } = this.props;
    closePopup();
  };

  private setScrollListener() {
    window.addEventListener("scroll", this.scrollListener);
  }

  private unsetScrollListener() {
    window.removeEventListener("scroll", this.scrollListener);
  }
}

export default InputView;
